import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./create-section.dialog.scoped.scss";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AppButtonComponent from "../../../../components/form/app-button";
import CloseIcon from "@material-ui/icons/Close";
import { showPopupToastMsg, showToastMsg } from "../../../../helpers";
import { services } from "../../../../services";
import AppRadioComponent from "../../../../components/form/app-radio";

const FIELD_LIMITS = {
  NAME: 80,
} as const;

interface SectionFormData {
  title: string;
  order: number;
  sequential: string;
}

interface FormErrors {
  title?: string;
  order?: string;
  [key: string]: string | undefined;
}

export interface CreateSectionDialogProps {
  show: boolean;
  onClose: (success?: boolean) => void;
  courseId: number;
  sectionId?: number;
  sectionData?: any;
}

export const CreateSectionDialog: React.FC<CreateSectionDialogProps> = ({
  show,
  onClose,
  courseId,
  sectionId,
  sectionData,
}) => {
  const { t } = useTranslation();

  const isEditMode = useMemo(() => sectionId != null && sectionData != null, [
    sectionId,
    sectionData,
  ]);

  const [formData, setFormData] = useState<SectionFormData>({
    title: "",
    order: 0,
    sequential: "NON_SEQUENTIAL",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  const validateField = useCallback(
    (title: keyof SectionFormData, value: unknown): string => {
      switch (title) {
        case "title":
          if (!value || String(value).trim() === "") {
            return t("EDUCATION_PAGE.SECTION_NAME_REQUIRED");
          }
          if (String(value).length > FIELD_LIMITS.NAME) {
            return t("EDUCATION_PAGE.SECTION_NAME_TOO_LONG", {
              max: FIELD_LIMITS.NAME,
            });
          }
          return "";
        case "order":
          if (value === null || value === undefined || Number(value) < 0) {
            return t("EDUCATION_PAGE.ORDER_REQUIRED");
          }
          return "";
        default:
          return "";
      }
    },
    [t],
  );

  const resetForm = useCallback(() => {
    setFormData({
      title: "",
      order: 0,
      sequential: "NON_SEQUENTIAL",
    });
    setErrors({});
  }, []);

  const handleClose = useCallback(
    (success: boolean = false) => {
      if (!saving) {
        resetForm();
        onClose(success);
      }
    },
    [saving, resetForm, onClose],
  );

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | { name?: string; value: unknown }
      >,
    ) => {
      const { name, value } = e.target;
      if (!name) return;

      const fieldName = name as keyof SectionFormData;
      console.log(value);
      const processedValue =
        fieldName === "order"
          ? String(value) === "0"
            ? "0"
            : String(Number(value))
          : typeof value === "string"
          ? value
          : String(value);

      setFormData((prev) => ({ ...prev, [fieldName]: processedValue }));

      const error = validateField(fieldName, processedValue);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    },
    [validateField],
  );

  const handleSubmit = useCallback(async () => {
    const finalErrors: FormErrors = {};
    (["title", "order"] as const).forEach((key) => {
      const err = validateField(key, formData[key]);
      if (err) finalErrors[key] = err;
    });

    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors);
      showToastMsg("error", t("EDUCATION_PAGE.PLEASE_FILL_ALL_FIELDS"));
      return;
    }

    if (!courseId) {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
      return;
    }

    const payload = {
      title: formData.title.trim(),
      order: formData.order,
      sequential: formData.sequential,
      courseId: String(courseId),
    };

    setSaving(true);
    try {
      let response: any;
      if (isEditMode && sectionId != null) {
        const { courseId: _omit, ...updatePayload } = payload;
        response = await services.courses.updateSection(
          String(sectionId),
          updatePayload,
        );
        if (response && response.message === "SUCCESS") {
          showToastMsg(
            "success",
            t("EDUCATION_PAGE.SECTION_UPDATED_SUCCESSFULLY"),
          );
          handleClose(true);
        } else {
          showPopupToastMsg(
            "error",
            response?.message || t("SOME_THING_WENT_WRONG"),
          );
        }
      } else {
        response = await services.courses.addSection(payload);
        if (response && response.message === "SUCCESS") {
          showToastMsg(
            "success",
            t("EDUCATION_PAGE.SECTION_CREATED_SUCCESSFULLY"),
          );
          handleClose(true);
        } else {
          showPopupToastMsg(
            "error",
            response?.message || t("SOME_THING_WENT_WRONG"),
          );
        }
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} section:`,
        error,
      );
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
  }, [
    courseId,
    formData,
    handleClose,
    isEditMode,
    sectionId,
    validateField,
    t,
  ]);

  const isFormValid = useMemo(() => {
    const titleOk =
      formData.title.trim() !== "" &&
      formData.title.length <= FIELD_LIMITS.NAME;
    const orderOk =
      formData.order !== null &&
      formData.order !== undefined &&
      !Number.isNaN(Number(formData.order)) &&
      Number(formData.order) >= 0;
    const noFieldErrors = !errors.title && !errors.order;
    return titleOk && orderOk && noFieldErrors;
  }, [formData.title, formData.order, errors.title, errors.order]);

  useEffect(() => {
    if (!show) return;
    if (isEditMode && sectionData) {
      setFormData({
        title: sectionData.title ?? "",
        order:
          sectionData.order !== undefined && sectionData.order !== null
            ? Number(sectionData.order)
            : 0,
        sequential: sectionData.sequential ?? "NON_SEQUENTIAL",
      });
      setErrors({});
    } else if (!isEditMode) {
      resetForm();
    }
  }, [show, isEditMode, sectionData, resetForm]);

  return (
    <Dialog
      open={show}
      onClose={() => !saving && handleClose(false)}
      maxWidth="md"
      fullWidth
      disableBackdropClick={saving}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
    >
      <DialogTitle
        style={{
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        disableTypography
      >
        <Typography variant="h2" className="m-0">
          {isEditMode
            ? t("EDUCATION_PAGE.EDIT_SECTION")
            : t("EDUCATION_PAGE.CREATE_SECTION")}
        </Typography>

        <IconButton
          aria-label={t("CLOSE")}
          edge="end"
          onClick={() => handleClose(false)}
          disabled={saving}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box className="title">
          <Typography
            variant="h3"
            className="font-weight-bold"
            color="textPrimary"
          >
            {t("EDUCATION_PAGE.SECTION_DETAILS")}
          </Typography>
        </Box>
        <Box className="grid-2 mb-3">
          <Typography variant="body1">
            {t("EDUCATION_PAGE.SECTION_NAME")}*
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              variant="standard"
              placeholder={t("EDUCATION_PAGE.SECTION_NAME")}
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={saving}
              inputProps={{
                maxLength: FIELD_LIMITS.NAME,
              }}
            />

            <Box className="hint d-flex align-items-center justify-content-between">
              <Typography variant="caption" color="error">
                {errors.title ?? ""}
              </Typography>
              <Typography
                variant="body2"
                className="px-2 text-right"
                style={{ flex: "auto" }}
                color="textPrimary"
              >
                {formData.title.length}/{FIELD_LIMITS.NAME}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box className="grid-2 mb-3">
          <Typography variant="body1">{t("EDUCATION_PAGE.ORDER")}</Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              variant="standard"
              placeholder={t("EDUCATION_PAGE.ORDER")}
              name="order"
              value={formData.order}
              onChange={handleInputChange}
              disabled={saving}
              inputProps={{
                min: 0,
              }}
              type="number"
            />

            <Box className="hint d-flex align-items-center justify-content-between">
              <Typography variant="caption" color="error">
                {errors.order ?? ""}
              </Typography>
            </Box>
            <Typography variant="body2" className="mt-3" color="textPrimary">
              {t("EDUCATION_PAGE.ORDER_SECTION_HINT")}
            </Typography>
          </Box>
        </Box>
        <Box className="title">
          <Typography
            variant="h3"
            className="font-weight-bold"
            color="textPrimary"
          >
            {t("EDUCATION_PAGE.WORKFLOW")}
          </Typography>
        </Box>
        <Box className="grid-2 mb-3">
          <Typography variant="body1">
            {t("EDUCATION_PAGE.PROCEDURAL_GUIDELINES")}
          </Typography>
          <Box className="section-workflow">
            <AppRadioComponent
              name="workflowSections"
              value={formData.sequential}
              onChange={(value: string) =>
                setFormData((prev: SectionFormData) => ({
                  ...prev,
                  sequential: value,
                }))
              }
              options={[
                {
                  value: "NON_SEQUENTIAL",
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>{t("EDUCATION_PAGE.NONE")}</strong>
                      <br />
                      {t(
                        "EDUCATION_PAGE.THE_SECTION_WILL_BE_UNLOCKED_AND_ALL_PARTICIPANTS_WILL_BE_GRANTED_ACCESS",
                      )}
                    </Typography>
                  ),
                },
                {
                  value: "SEQUENTIAL",
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>{t("EDUCATION_PAGE.SEQUENTIAL")}</strong>
                      <br />
                      {t(
                        "EDUCATION_PAGE.THIS_SECTION_BECOMES_AVAILABLE_IMMEDIATELY_AFTER_THE_PREVIOUS_ELEMENT_IS_COMPLETED",
                      )}
                    </Typography>
                  ),
                },
              ]}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        style={{
          borderTop: "1px solid var(--border)",
          padding: "16px 24px",
          gap: "1rem",
        }}
      >
        <AppButtonComponent
          variant="text"
          color="secondary"
          onClick={() => handleClose(false)}
          disabled={saving}
        >
          {t("CANCEL")}
        </AppButtonComponent>
        <AppButtonComponent
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving || !isFormValid}
        >
          {isEditMode ? t("SAVE") : t("CREATE")}
        </AppButtonComponent>
      </DialogActions>
    </Dialog>
  );
};
